<template>
  <!--
    WHAT: Editor for a metric's compute shape — aggregation type, primary-key
          (grain) columns, and numerator ("value") conditions. This is the config
          that turns a join graph into a countable metric.
    WHERE: [reused verbatim from v1] Wrapped by AggregationSection.vue (the metric
           "Aggregation" detail tab). Typically hosted in StudioJoinBuilder's
           #config slot / a section card.
    CONTRACT:
      props:
        modelValue (Object, req) — { aggregation_type, primary_key_columns,
                                     value_conditions }
        outputColumns (Array)    — [{ col, workflow_id, label }] from the join, for the pk +
                                   condition-column pickers
      emits: update:modelValue
    NOTES: [reused verbatim from v1] shared component — do NOT restyle without
           checking other callers. Gotchas: local state is mirrored from
           modelValue and re-synced via a deep watcher (so a parent object
           REPLACEMENT/reload re-hydrates); defaults are aggregation 'percentage',
           pk ['BENE_MBI_ID'], and one value condition VALUE IS NOT NULL; empty
           condition columns are dropped on emit.
  -->
  <div>
    <div class="text-xs font-semibold text-gray-600 mb-2.5">Aggregation</div>
    <div class="flex flex-wrap gap-1.5 mb-4.5" style="margin-bottom:18px;">
      <button
        v-for="a in aggOptions"
        :key="a"
        class="font-mono text-[11.5px] font-medium rounded-lg px-3 py-1.5 border"
        :style="aggregation === a
          ? { color:'#fff', background:'#2563EB', borderColor:'#2563EB' }
          : { color:'#4B5563', background:'#F9FAFB', borderColor:'#E5E7EB' }"
        @click="setAggregation(a)"
      >{{ a }}</button>
    </div>

    <div class="text-xs font-semibold text-gray-600 mb-2.5">Primary key</div>
    <div class="flex flex-wrap items-center gap-1.5 mb-4.5" style="margin-bottom:18px;">
      <span
        v-for="(pk, idx) in pkCols"
        :key="pk"
        class="inline-flex items-center gap-1 font-mono text-[11.5px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1"
      >
        {{ pk }}
        <button class="text-blue-400 hover:text-red-500 leading-none" @click="removePk(idx)">×</button>
      </span>
      <select class="font-mono text-[11.5px] text-gray-400 border border-dashed border-gray-300 rounded-lg px-2 py-1 bg-transparent" @change="addPk($event)">
        <option value="">＋ add</option>
        <option v-for="col in availablePkColumns" :key="col.col" :value="col.col">#{{ col.label }} {{ col.col }}</option>
      </select>
    </div>

    <div class="flex items-center justify-between mb-2.5">
      <span class="text-xs font-semibold text-gray-600">Numerator conditions</span>
      <button class="font-mono text-[11px] text-blue-600 hover:text-blue-700" @click="addValueCondition">＋ add</button>
    </div>
    <div class="flex flex-col gap-2">
      <div v-for="(vc, i) in valueConditions" :key="i" class="flex items-center gap-1.5">
        <select
          v-model="vc.column"
          class="font-mono text-[11.5px] text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1 min-w-0"
          @change="emit"
        >
          <option value="">— column —</option>
          <option v-for="col in outputColumns" :key="col.col" :value="col.col">#{{ col.label }} {{ col.col }}</option>
        </select>
        <select
          v-model="vc.op"
          class="font-mono text-[11.5px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5 flex-shrink-0"
          @change="emit"
        >
          <option value="=">=</option>
          <option value="!=">!=</option>
          <option value="<">&lt;</option>
          <option value=">">&gt;</option>
          <option value="<=">&lt;=</option>
          <option value=">=">&gt;=</option>
          <option value="IS NOT NULL">NOT NULL</option>
          <option value="IS NULL">NULL</option>
        </select>
        <input
          v-if="vc.op !== 'IS NOT NULL' && vc.op !== 'IS NULL'"
          v-model="vc.value"
          type="text"
          placeholder="value"
          class="font-mono text-[11.5px] text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 flex-1 min-w-0"
          style="min-width:54px;"
          @blur="emit"
          @keydown.enter="emit"
        />
        <button v-if="valueConditions.length > 1" class="text-gray-300 hover:text-red-400 text-sm flex-shrink-0" @click="removeValueCondition(i)">×</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MetricComputePanel',
  props: {
    // v-model: { aggregation_type, primary_key_columns, value_conditions }
    modelValue: { type: Object, required: true },
    outputColumns: { type: Array, default: () => [] },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      aggOptions: ['percentage', 'count', 'inverse_count', 'sum', 'sum_ratio'],
      aggregation: this.modelValue.aggregation_type || 'percentage',
      pkCols: [...(this.modelValue.primary_key_columns || ['BENE_MBI_ID'])],
      valueConditions: (this.modelValue.value_conditions && this.modelValue.value_conditions.length)
        ? this.modelValue.value_conditions.map((c) => ({ ...c }))
        : [{ column: 'VALUE', op: 'IS NOT NULL', value: '' }],
    };
  },
  computed: {
    availablePkColumns() {
      return this.outputColumns.filter((c) => !this.pkCols.includes(c.col));
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(v) {
        // keep local state synced if parent replaces the object (e.g. reload)
        this.aggregation = v.aggregation_type || 'percentage';
        this.pkCols = [...(v.primary_key_columns || ['BENE_MBI_ID'])];
        this.valueConditions = (v.value_conditions && v.value_conditions.length)
          ? v.value_conditions.map((c) => ({ ...c }))
          : [{ column: 'VALUE', op: 'IS NOT NULL', value: '' }];
      },
    },
  },
  methods: {
    emit() {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        aggregation_type: this.aggregation,
        primary_key_columns: [...this.pkCols],
        value_conditions: this.valueConditions.filter((c) => c.column).map((c) => ({ column: c.column, op: c.op, value: c.value })),
      });
    },
    setAggregation(a) { this.aggregation = a; this.emit(); },
    addValueCondition() { this.valueConditions.push({ column: '', op: '=', value: '' }); },
    removeValueCondition(i) { this.valueConditions.splice(i, 1); this.emit(); },
    addPk(e) {
      const val = e.target.value;
      if (val && !this.pkCols.includes(val)) { this.pkCols.push(val); this.emit(); }
      e.target.value = '';
    },
    removePk(idx) { this.pkCols.splice(idx, 1); this.emit(); },
  },
};
</script>
